import {
    HTTP_INTERCEPTORS,
    HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivationEnd, Navigation, Router, Event as RouterEvent } from '@angular/router';
import { BehaviorSubject, Observable, Subject, filter, takeUntil } from 'rxjs';
import { TokenService } from 'services/token/token.service';
import { RoutePath } from 'src/app/common/constant/route-path';
import { Logger } from 'src/app/common/utility/logger/logger';
import { RouteUtilities } from 'src/app/common/utility/route/RouteUtilities';
import { Strings } from 'src/app/common/utility/string/string';
import { RoutingService } from 'src/app/routing/routing.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

    private unsubscribe: Subject<void> = new Subject();
    private pathChange: BehaviorSubject<string> = new BehaviorSubject(Strings.Empty)

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const path: string = this.pathChange.getValue()
        const anonymousRoute: boolean = this.AnonymousRoute(path)
        if (!anonymousRoute && !this.tokenService.valid) {
            Logger.trace('Authorization token expired. Logging user out');
            this.logOutUser('Token Expired');
        }
        return next.handle(request)
    }

    private logOutUser(reason: string): void {
        if (!this.LoginRoute) {
            this.routingService.clearRouteCache();
        } else {
            this.routingService.cachedRoute = this.router.url
            Logger.trace(`Caching route before logging user out: ${this.router.url}`);
        }
        Logger.trace(`AuthenticationInterceptor: logging User out. Reason: ${reason}`);
        this.routingService.navigate(RoutePath.Login);
    }

    private get LoginRoute(): boolean {
        return this.router.url === RoutePath.Login
    }

    private AnonymousRoute(path: string): boolean {
        const navigation: Navigation | null = this.router.getCurrentNavigation()
        Logger.debug(`Current Route: ${navigation?.finalUrl}`)
        return RouteUtilities.AnonymousRoute(path, this.router.config)
    }

    constructor(
        private router: Router,
        private tokenService: TokenService,
        private routingService: RoutingService,
    ) {
        this.listenForPath()
        Logger.debug(`Current Route: ${router.url}`)
    }

    ngOnDestroy(): void {
        this.unsubscribe.next()
        this.unsubscribe.complete();
    }

    private listenForPath(): void {
        this.router.events.pipe(
            takeUntil(this.unsubscribe),
            filter((event: RouterEvent) => this.ActivationEndOnly(event))
        ).subscribe((event: RouterEvent) => {
            const activationEnd: ActivationEnd = <ActivationEnd>event
            const path: string = activationEnd.snapshot.url[0].path
            this.pathChange.next(path)
            Logger.debug(`Current Route Event: ${event}`)
        });
    }

    private ActivationEndOnly(event: RouterEvent): boolean {
        return event instanceof ActivationEnd
    }
}

export const AuthenticatedInterceptor: any = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthenticationInterceptor,
    multi: true
}
